import { DAY } from "./time.ts";
import { clip, type Reason, reason } from "./reasons.ts";
import { normalizeCommunity, queryTerms, type Community } from "./listening.ts";

// The first read of a product: rules that run without a model, and the
// cleaning applied to a model's proposal. Ported from the archived
// server/product/analysis.mjs, taxonomy.mjs and sources/github.mjs.

// ------------------------------------------------------------- categories

export const CATEGORIES = ["developer_tool", "ai", "ai_agent", "devops", "data", "security", "seo_marketing", "content_media", "productivity", "community", "career", "education", "consumer", "commerce", "marketplace", "local_service", "professional_service", "health_wellness", "travel_hospitality"] as const;
export type Category = (typeof CATEGORIES)[number];

const CATEGORY_RULES: [Category, RegExp][] = [
  ["commerce", /\b(?:ecommerce|e-commerce|retail|store|shop|shopping|checkout|cart|merchandise)\b|电商|商店|购物|零售/i],
  ["marketplace", /\bmarketplace\b|双边平台|交易平台/i],
  ["local_service", /\b(?:local service|plumber|plumbing|electrician|salon|dentist|repair service)\b|本地服务|水管|电工|沙龙|维修服务/i],
  ["professional_service", /\b(?:consulting|consultancy|accounting|legal service|agency)\b|咨询服务|会计|法律服务|事务所/i],
  ["health_wellness", /\b(?:health|wellness|fitness|nutrition|therapy)\b|健康|健身|营养|康复/i],
  ["travel_hospitality", /\b(?:travel|tourism|hotel|hospitality|restaurant)\b|旅游|酒店|餐厅/i],
  ["ai_agent", /\bagents?\b|agentic|智能体/i],
  ["ai", /\bai\b|\bllms?\b|\bgpt|\bmodels?\b|machine learning|\bml\b|人工智能|大模型|模型|生成式/i],
  ["seo_marketing", /\bseo\b|marketing|backlinks?|keyword research|rank tracking|营销/i],
  ["content_media", /video|drama|image|photo|\bmedia\b|music|audio|podcast|\bcontent\b|视频|短剧|图片|音频/i],
  ["career", /\bjobs?\b|career|hiring|recruit|resume|求职|招聘|简历/i],
  ["community", /directory|social|communit|\bnetwork\b|makers?\b|社区|目录/i],
  [
    "devops",
    /devops|infra|deploy|kubernetes|\bk8s\b|docker|\bcloud\b|\blogs?\b|logging|monitor|observab|self.?host|运维|部署|日志|监控|自托管/i,
  ],
  ["data", /\bdata\b|database|\bsql\b|analytics|\betl\b|数据/i],
  ["security", /security|\bauth\b|privacy|vulnerab|安全|隐私/i],
  [
    "developer_tool",
    /developer|dev ?tools?|\bcli\b|\bsdk\b|\bapi\b|coding|code review|terminal|\bide\b|编程|开发者|命令行/i,
  ],
  ["productivity", /productivity|\bnotes?\b|todo|calendar|workflow|效率|笔记/i],
  ["education", /educat|learn|course|tutorial|guide|学习|教育|课程/i],
  ["consumer", /live better|lifestyle|personal|health|habit|生活|健康/i],
];

/** Category keys found in any of the given texts, in vocabulary order. */
export function normalizeCategories(input: unknown): Category[] {
  const text = (Array.isArray(input) ? input : [input])
    .flat()
    .filter(Boolean)
    .join(" ")
    .replace(/[_-]+/g, " ");
  const out: Category[] = [];
  for (const [key, re] of CATEGORY_RULES) if (re.test(text)) out.push(key);
  if (out.includes("ai_agent") && !out.includes("ai")) out.push("ai");
  return out;
}

/** Keeps only known keys, without duplicates. */
export const cleanCategories = (keys: unknown): Category[] =>
  [...new Set(Array.isArray(keys) ? keys : [])].filter((k): k is Category => typeof k === "string" && (CATEGORIES as readonly string[]).includes(k));

// ----------------------------------------------------------------- README

export interface ReadmeFeatures {
  length: number;
  images: number;
  gif: boolean;
  video: boolean;
  install: boolean;
  demo_in_first_screen: boolean;
  badges: number;
}

// README heuristics describe what a visitor sees first; they are not a
// judgement of product quality.
export function readmeFeatures(text: unknown): ReadmeFeatures | null {
  if (typeof text !== "string" || !text.trim()) return null;
  const head = text.slice(0, 6000);
  return {
    length: text.length,
    images: (text.match(/!\[[^\]]*\]\([^)]+\)|<img\s/gi) || []).length,
    gif: /\.gif\b/i.test(head),
    video: /\.(mp4|webm|mov)\b|youtube\.com\/watch|youtu\.be\/|<video/i.test(head),
    install:
      /npm (i|install)|pnpm(?:@[\w.+-]+)? (add|i)|yarn add|bun add|pip install|uv (add|tool install)|brew install|cargo install|go install|npx |uvx |docker run|curl .*\| *(sh|bash)/i.test(
        text,
      ),
    demo_in_first_screen: /!\[[^\]]*\]\([^)]+\)|<img\s|<video/i.test(text.slice(0, 2500)),
    badges: (head.match(/shields\.io|badge/gi) || []).length,
  };
}

// Badges and CI images are not a demo.
const NOT_DEMO =
  /shields\.io|badgen|badge|travis-ci|circleci|codecov|coveralls|github\.com\/[^)]*\/workflows|actions\/workflows|star-history|contrib\.rocks/i;

/** Whether the README shows the product working before any explanation. */
export function readmeHasDemo(text: unknown): boolean {
  const head = String(text || "").slice(0, 3000);
  if (/\.(gif|mp4|webm|mov)\b|<video|youtube\.com\/watch|youtu\.be\//i.test(head)) return true;
  const images = [
    ...head.matchAll(/!\[[^\]]*\]\(([^)\s]+)[^)]*\)/g),
    ...head.matchAll(/<img[^>]+src=["']([^"']+)["']/gi),
  ].map((m) => m[1]);
  return images.some((src) => !NOT_DEMO.test(src));
}

/** Visible text of a page: no scripts, styles or markup. */
export function pageText(html: unknown): string {
  return String(html || "")
    .replace(/<(script|style|noscript|svg|template)[\s\S]*?<\/\1>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 12000);
}

// ------------------------------------------------------------- readiness

export interface ReadSources {
  source_of_truth?: "website" | "repository";
  website_status?: "current" | "previous_version";
  repository_files?: { path: string; text: string }[];
  page: { url: string; title: string; description: string; text: string; html: string } | null;
  repo: { full_name: string; description: string | null; topics: string[]; language: string | null; homepage: string | null; stars: number | null } | null;
  readme: { text: string } | null;
  releases: { published_at: string | null }[];
  releases_read: boolean;
  has_repo: boolean;
}

export interface Readiness {
  has_site: boolean;
  has_repo: boolean;
  readme_read: boolean;
  readme_demo: boolean | null;
  install_one_liner: boolean | null;
  site_comparison: boolean | null;
  site_demo: boolean | null;
  site_contact?: boolean | null;
  site_purchase?: boolean | null;
  site_booking?: boolean | null;
  site_subscription?: boolean | null;
  site_download?: boolean | null;
  site_contribution?: boolean | null;
  releases_read: boolean;
  last_release_at: string | null;
  days_since_release: number | null;
}

/**
 * Plain checks of what a first-time visitor sees. None is a quality score.
 * `null` means not checked (nothing to read), never "no".
 */
export function readinessOf(sources: ReadSources, now: number): Readiness {
  // A previous website describes an earlier product, not this version's readiness.
  const page = sources.website_status === "previous_version" ? null : sources.page;
  const text = page?.text || "";
  const html = page?.html || "";
  // Read only action elements. A feature mentioned in prose, script or style
  // is not evidence of a visible next step; these checks never submit a form.
  const markup = html.replace(/<!--[\s\S]*?(?:-->|$)/g, " ").replace(/<(script|style|noscript|template)[\s\S]*?<\/\1>/gi, " ");
  const targetOf = (attributes: string) => {
    const match = /(?:^|\s)(?:href|action)\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'=<>]+))/i.exec(attributes);
    return match?.[1] ?? match?.[2] ?? match?.[3] ?? "";
  };
  const actions = [...markup.matchAll(/<(a|button)\b([^>]*)>([\s\S]*?)<\/\1>/gi)].map((match) => ({
    label: pageText(match[3]),
    target: targetOf(match[2]),
  }));
  for (const form of markup.matchAll(/<form\b([^>]*)>/gi)) actions.push({ label: "", target: targetOf(form[1]) });
  const action = (label: RegExp, target: RegExp): boolean | null => page
    ? actions.some((item) => label.test(item.label) || target.test(item.target))
    : null;
  const last = (sources.releases || [])
    .map((r) => Date.parse(r.published_at ?? ""))
    .filter(Number.isFinite)
    .sort((a, b) => b - a)[0];
  return {
    has_site: Boolean(sources.page),
    has_repo: sources.has_repo,
    readme_read: Boolean(sources.readme),
    readme_demo: sources.readme ? readmeHasDemo(sources.readme.text) : null,
    install_one_liner: sources.readme ? Boolean(readmeFeatures(sources.readme.text)?.install) : null,
    site_comparison: page
      ? /\b(vs\.?|versus|alternatives?|compared? (to|with)|comparison)\b|对比|替代/i.test(text)
      : null,
    site_demo: page
      ? /<video|youtube\.com\/embed|player\.vimeo|loom\.com\/(share|embed)|\.gif\b/i.test(html)
      : null,
    site_contact: action(/\b(?:contact|talk to|get in touch|request a demo)\b|联系我们|联系销售/i, /^(?:mailto:|tel:)|(?:^|[\/\-])(?:contact|contact-sales|request-demo)(?:[\/\-?#]|$)/i),
    site_purchase: action(/\b(?:buy|shop|add to cart|checkout)\b|购买|加入购物车|结账/i, /(?:^|[\/\-])(?:shop|cart|checkout|buy)(?:[\/\-?#]|$)/i),
    site_booking: action(/\b(?:book|reserve|schedule|appointment)\b|预约|预订/i, /(?:^|[\/\-])(?:book|booking|reserve|schedule|appointment)(?:[\/\-?#]|$)/i),
    site_subscription: action(/\b(?:subscribe|join (?:the |our )?newsletter|sign up)\b|订阅/i, /(?:^|[\/\-])(?:subscribe|newsletter|signup|sign-up)(?:[\/\-?#]|$)/i),
    site_download: action(/\b(?:download|install|get the app)\b|下载|安装/i, /(?:apps\.apple\.com|play\.google\.com)|(?:^|[\/\-])(?:download|install)(?:[\/\-?#]|$)/i),
    site_contribution: action(/\b(?:contribute|contributing)\b|参与贡献|贡献指南/i, /(?:^|[\/\-])(?:contribute|contributing)(?:[\/\-.?#]|$)/i),
    releases_read: sources.releases_read,
    last_release_at: last ? new Date(last).toISOString() : null,
    days_since_release: last ? Math.floor((now - last) / DAY) : null,
  };
}

/** Which source checks apply, based on the person's business model (design/plan.md §5.3 R2a item 11). */
export const READINESS_CHECKS = ["readme_demo", "install_one_liner", "site_comparison", "site_demo", "site_contact", "site_purchase", "site_booking", "site_subscription", "site_download", "site_contribution"] as const;
export type ReadinessCheck = (typeof READINESS_CHECKS)[number];
const MODEL_CHECKS: Record<BusinessModel, readonly ReadinessCheck[]> = {
  self_serve_software: ["readme_demo", "install_one_liner", "site_comparison", "site_demo"],
  developer_tool: ["readme_demo", "install_one_liner", "site_comparison", "site_demo"],
  sales_led_b2b: ["site_comparison", "site_contact"],
  consumer_app: ["site_download", "site_demo"],
  extension: ["site_download", "site_demo"],
  website: ["site_comparison", "site_subscription"],
  ecommerce: ["site_purchase", "site_comparison"],
  marketplace: ["site_purchase", "site_contact"],
  content_creator: ["site_subscription"],
  local_service: ["site_booking", "site_contact"],
  open_source: ["readme_demo", "install_one_liner", "site_comparison", "site_demo", "site_contribution"],
};

/** Old reads that never checked a field remain unknown, including after a model change. */
export function readinessChecks(model: BusinessModel | null, readiness: Readiness | null | undefined, websiteStatus = "current"): { key: ReadinessCheck; value: boolean | null }[] {
  const keys: readonly ReadinessCheck[] = model ? MODEL_CHECKS[model] : ["site_comparison", "site_contact"];
  return keys.map((key) => ({ key, value: websiteStatus === "previous_version" && key.startsWith("site_") ? null : typeof readiness?.[key] === "boolean" ? readiness[key]! : null }));
}

// --------------------------------------------------------- the proposal

export interface WatchProposal {
  queries: { value: string; why: Reason }[];
  communities: (Community & { why: Reason })[];
  competitors: { name: string; website: string; github: string; why: Reason }[];
}

export interface ProfileProposal extends WatchProposal {
  one_liner: string;
  problem: string;
  audience: string;
  not_for: string;
  categories: Category[];
}

// Where people with a problem like this ask for help. Rule mode only.
const SUBREDDITS: [RegExp, string][] = [
  [/self.?host|homelab/i, "r/selfhosted"],
  [/devops|kubernetes|docker|deploy|\blogs?\b|observab|monitor/i, "r/devops"],
  [/\bllms?\b|local model|ollama|gguf/i, "r/LocalLLaMA"],
  [/\bseo\b|backlink|keyword/i, "r/SEO"],
  [/\bsaas\b|indie|startup|founder/i, "r/SaaS"],
  [/python/i, "r/Python"],
  [/javascript|typescript|node\.?js|react/i, "r/javascript"],
  [/\brust\b/i, "r/rust"],
  [/golang|\bgo\b/i, "r/golang"],
  [/data|analytics|\bsql\b|etl/i, "r/dataengineering"],
  [/security|privacy/i, "r/netsec"],
  [/productivity|notes|todo/i, "r/productivity"],
  [/\bcli\b|terminal|command line/i, "r/commandline"],
  [/open.?source/i, "r/opensource"],
];

// Where people ask precise how-to questions. Rule mode only.
const STACK_EXCHANGE: [RegExp, string][] = [
  [/\bcli\b|terminal|command line|shell|\blogs?\b|linux|unix/i, "se/unix"],
  [/devops|kubernetes|docker|sysadmin|nginx|server/i, "se/serverfault"],
  [/\bapi\b|\bsdk\b|library|framework|javascript|typescript|python|\brust\b|golang/i, "se/stackoverflow"],
];

const GENERIC_TOPICS = new Set(
  "cli ai llm llms open-source opensource hacktoberfest typescript javascript python go golang rust java c cpp react nodejs node tool tools app library framework awesome github developer-tools devtools self-hosted selfhosted mcp agent agents ai-agents chatgpt openai claude command-line command-line-tool commandline terminal tui shell bash zsh linux macos unix windows console productivity utility utilities".split(
    " ",
  ),
);

/** What rules alone can say about the product. */
export function rulesProposal(
  sources: ReadSources,
  knownCompetitors: { value: string; reason?: string }[] = [],
): ProfileProposal {
  const repositoryPrimary = sources.source_of_truth === "repository";
  const readmeDescription = sources.readme?.text.split(/\n\s*\n/)
    .map((p) => p.trim()).find((p) => p.length > 30 && !/^(?:#|!\[|<|\[!|```)/.test(p))?.replace(/\s+/g, " ") ?? "";
  const description = repositoryPrimary ? readmeDescription : sources.page?.description || sources.repo?.description || "";
  const topics = repositoryPrimary ? [] : sources.repo?.topics || [];
  const corpus = [description, topics.join(" "), repositoryPrimary ? sources.readme?.text.slice(0, 6000) : sources.page?.text?.slice(0, 2000)].join(" ");
  const queries = topics
    .filter((t) => !GENERIC_TOPICS.has(t))
    .map((t) => t.replace(/[-_]+/g, " "))
    // A topic made of common words alone ("less") could never match a thread.
    .filter((t) => queryTerms(t).length > 0)
    .slice(0, 4)
    .map((t) => ({ value: t, why: reason("from_repo_topic") }));
  const communities: WatchProposal["communities"] = [];
  // Developers first: Hacker News is the one place every dev tool can use.
  if (sources.has_repo || /developer|\bcli\b|\bapi\b/i.test(corpus))
    communities.push({ value: "hackernews", scannable: true, why: reason("developer_product_on_hn") });
  // One Stack Exchange site, where the precise how-to questions are.
  const site = STACK_EXCHANGE.find(([re]) => re.test(corpus));
  if (site) communities.push({ value: site[1], scannable: true, why: reason("how_to_questions_on_stackexchange") });
  for (const [re, sub] of SUBREDDITS)
    if (re.test(corpus) && !communities.some((c) => c.value === sub))
      communities.push({ value: sub, scannable: true, why: reason("related_words_on_site") });
  return {
    one_liner: clip(description, 160),
    problem: "",
    audience: "",
    not_for: "",
    categories: normalizeCategories(corpus).slice(0, 3),
    queries,
    communities: communities.slice(0, 4),
    competitors: knownCompetitors.slice(0, 4).map((c) => ({
      name: c.value.split("/")[1] || c.value,
      website: "",
      github: /^[\w.-]+\/[\w.-]+$/.test(c.value) ? c.value : "",
      why: reason("shares_repo_topics", undefined, c.reason),
    })),
  };
}

/** Names the product goes by: its name, its domain and its repository. */
export function ownNames(product: { name: string; url: string | null; repo?: string | null }): Set<string> {
  const names = new Set<string>();
  const add = (v: unknown) => {
    const s = String(v || "")
      .trim()
      .toLowerCase();
    if (s.length >= 3) names.add(s);
  };
  add(product.name);
  try {
    if (product.url) add(new URL(product.url).hostname.replace(/^www\./, "").split(".")[0]);
  } catch {
    // No usable URL.
  }
  if (product.repo) add(product.repo.split("/")[1]);
  return names;
}

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

export interface ModelProposal {
  one_liner?: unknown;
  problem?: unknown;
  audience?: unknown;
  not_for?: unknown;
  categories?: unknown;
  queries?: { text?: unknown; why?: unknown }[];
  communities?: { name?: unknown; why?: unknown }[];
  competitors?: { name?: unknown; website?: unknown; github?: unknown; why?: unknown }[];
}

/** Trims and validates a model's proposal against what the product allows. */
export function cleanModelProposal(
  data: ModelProposal,
  product: { name: string; url: string | null; repo?: string | null },
): ProfileProposal {
  const own = String(product.name || "").toLowerCase();
  const self = ownNames(product);
  const isSelf = (name: string) => {
    const n = name.trim().toLowerCase();
    return [...self].some((s) => n === s || n.startsWith(`${s} `) || n.startsWith(`${s}(`));
  };
  const seen = new Set<string>();
  const model = (why: unknown) => reason("model", undefined, clip(why, 160));
  return {
    one_liner: clip(data.one_liner, 160),
    problem: clip(data.problem, 240),
    audience: clip(data.audience, 200),
    not_for: clip(data.not_for, 200),
    categories: cleanCategories(data.categories).slice(0, 3),
    queries: (data.queries || [])
      .map((q) => ({ value: clip(q.text, 80), why: model(q.why) }))
      .filter((q) => {
        const key = q.value.toLowerCase();
        if (q.value.length < 3 || seen.has(key) || !queryTerms(q.value).length) return false;
        if (own.length >= 3 && key.includes(own)) return false;
        if ([...self].some((s) => new RegExp(`\\b${escapeRe(s)}\\b`, "i").test(q.value))) return false;
        seen.add(key);
        return true;
      })
      .slice(0, 6),
    communities: (data.communities || [])
      .map((c) => {
        const n = normalizeCommunity(c.name);
        return n ? { ...n, why: model(c.why) } : null;
      })
      .filter((c): c is NonNullable<typeof c> => c !== null)
      .filter((c, i, all) => all.findIndex((x) => x.value === c.value) === i)
      .slice(0, 4),
    competitors: (data.competitors || [])
      .map((c) => {
        const github = String(c.github || "")
          .replace(/^https?:\/\/(www\.)?github\.com\//i, "")
          .replace(/\/$/, "");
        let website = "";
        try {
          const u = new URL(String(c.website || ""));
          if (/^https?:$/.test(u.protocol)) website = u.href;
        } catch {
          // Not a URL.
        }
        return {
          name: clip(c.name, 60),
          website,
          github: /^[\w.-]+\/[\w.-]+$/.test(github) ? github : "",
          why: model(c.why),
        };
      })
      // A name, not a description; never the product itself.
      .filter((c) => c.name.length >= 1 && c.name.length <= 40 && !isSelf(c.name))
      .slice(0, 4),
  };
}

// ------------------------------------------------------------------ stage

/** Where a product is in its life (design/product-vision.md §2.2). */
export const PRODUCT_STAGES = ["in_development", "launched", "early_traction", "scaling", "mature"] as const;
export type ProductStage = (typeof PRODUCT_STAGES)[number];

/**
 * How a product grows (design/product-vision.md §2.1): each model has its own
 * growth engines and key numbers. The person picks one as the product's type;
 * each names one kind of product (`product-kinds.ts`, docs/product-decisions.md
 * 2026-11-01).
 */
export const BUSINESS_MODELS = ["open_source", "developer_tool", "self_serve_software", "sales_led_b2b", "consumer_app", "extension", "website", "ecommerce", "marketplace", "content_creator", "local_service"] as const;
export type BusinessModel = (typeof BUSINESS_MODELS)[number];

/**
 * The stage a product's sources imply (design/plan.md §4.2.2): one whose
 * current version is read from its repository, or whose website describes an
 * earlier version, is still in development.
 */
export function stageOf(product: { sourceOfTruth: string; websiteStatus: string }): ProductStage {
  return product.sourceOfTruth === "repository" || product.websiteStatus === "previous_version" ? "in_development" : "launched";
}

/** The stage a person set; until they set one, the stage the sources imply. */
export function currentStage(product: { stage: string | null; sourceOfTruth: string; websiteStatus: string }): { value: ProductStage; by: "person" | "sources" } {
  return (PRODUCT_STAGES as readonly string[]).includes(product.stage ?? "")
    ? { value: product.stage as ProductStage, by: "person" }
    : { value: stageOf(product), by: "sources" };
}

/** Whether a playbook suits a product's stage: one that names no stages suits every stage (design/architecture.md §6). */
export const playbookFitsStage = (stages: readonly string[], stage: ProductStage) => stages.length === 0 || stages.includes(stage);

/** The business model a person set, or null; a stored value no longer in the list counts as not set. */
export const businessModelOf = (product: { businessModel: string | null }): BusinessModel | null =>
  (BUSINESS_MODELS as readonly string[]).includes(product.businessModel ?? "") ? (product.businessModel as BusinessModel) : null;

// ------------------------------------------------------------------ facts

/** How a fact is known: confirmed by a person, observed by a connector, inferred by a model or rule. */
export const FACT_STATUS = ["confirmed", "observed", "inferred"] as const;
export type FactStatus = (typeof FACT_STATUS)[number];

/** The profile facts the first read proposes and a person confirms. */
export const PROFILE_FACTS = ["one_liner", "problem", "audience", "not_for", "categories"] as const;
export type ProfileFact = (typeof PROFILE_FACTS)[number];
